import React, { createContext, useCallback, useContext, useState } from 'react';
import TalentProfileModal from './TalentProfileModal';

const TalentProfileModalContext = createContext({ open: () => {} });

/**
 * Wraps the app so any descendant can call useTalentProfileModal().open(talentId, { surface })
 * to surface a full-profile modal without prop-drilling.
 *
 * Iter-227m: `surface` is an optional analytics tag describing where the
 * modal was launched from. Accepted values:
 *   'discovery' | 'search_results' | 'applicant_list' | 'ai_match'
 *   | 'direct_link' | 'public_link' | 'modal' | 'message_thread'
 * Defaults to 'modal' on the backend if omitted.
 */
export const TalentProfileModalProvider = ({ children }) => {
  const [talentId, setTalentId] = useState(null);
  const [surface, setSurface] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback((id, opts = {}) => {
    if (!id) return;
    setTalentId(String(id));
    setSurface(opts.surface || null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <TalentProfileModalContext.Provider value={{ open, close }}>
      {children}
      <TalentProfileModal
        talentId={talentId}
        surface={surface}
        open={isOpen}
        onOpenChange={(o) => { if (!o) close(); }}
      />
    </TalentProfileModalContext.Provider>
  );
};

export const useTalentProfileModal = () => useContext(TalentProfileModalContext);

export default TalentProfileModalProvider;
